import React, { useState, useEffect } from 'react';
import { HeadacheFormData } from '../types';
import MedicationAutocomplete from './MedicationAutocomplete';
import './HeadacheForm.css';

interface HeadacheFormProps {
  onSubmit: (data: HeadacheFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<HeadacheFormData>;
}

// Función helper para formatear fecha local para input datetime-local
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Función para parsear fecha del backend sin conversión de timezone
const parseDateFromBackend = (dateStr: string): string => {
  // El backend devuelve "2024-12-29 14:30:00" o "2024-12-29T14:30:00"
  // Lo convertimos a formato datetime-local "2024-12-29T14:30" sin interpretar timezone
  if (!dateStr) return '';

  // Reemplazar espacio por T si es necesario y tomar solo YYYY-MM-DDTHH:MM
  const normalized = dateStr.replace(' ', 'T');
  return normalized.substring(0, 16); // "2024-12-29T14:30"
};

const HeadacheForm: React.FC<HeadacheFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {}
}) => {
  const [formData, setFormData] = useState<HeadacheFormData>({
    location: initialData.location || '',
    intensity: initialData.intensity || '',
    medication_taken: initialData.medication_taken !== undefined ? initialData.medication_taken : null,
    medication: initialData.medication || '',
    medication_time: initialData.medication_time || '',
    date: initialData.date
      ? parseDateFromBackend(initialData.date) // Editar: usar fecha del backend tal cual
      : formatDateForInput(new Date()), // Nuevo: usar fecha/hora actual local
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Actualizar fecha al montar el componente si es nuevo registro
  useEffect(() => {
    if (!initialData.date) {
      // Para nuevo registro, establecer la fecha/hora actual
      const now = new Date();
      const currentDateTime = formatDateForInput(now);
      console.log('HeadacheForm - Hora actual del dispositivo:', now.toLocaleString('es-ES'));
      console.log('HeadacheForm - Fecha formateada:', currentDateTime);
      setFormData(prev => ({
        ...prev,
        date: currentDateTime
      }));
    }
  }, []); // Solo al montar

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'location' && (e.target as HTMLSelectElement).multiple) {
      // Manejar select múltiple
      const select = e.target as HTMLSelectElement;
      const selectedValues = Array.from(select.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        location: selectedValues.join(', ')
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validar que se haya seleccionado la intensidad
      if (!formData.intensity) {
        throw new Error('Por favor, selecciona la intensidad del dolor');
      }

      // Validar que se haya seleccionado si tomó medicación o no
      if (formData.medication_taken === null) {
        throw new Error('Por favor, selecciona si tomaste medicación o no');
      }

      // Si tomó medicación pero no especificó hora, usar la hora actual
      let medicationTime = formData.medication_time;
      if (formData.medication_taken && !medicationTime) {
        const now = new Date();
        medicationTime = now.toTimeString().slice(0, 5); // HH:MM formato local
      }

      // Preparar datos para envío
      // Enviar la fecha tal cual del input datetime-local (formato: "2024-12-29T14:30")
      // Añadimos ":00" para los segundos
      const submitData: HeadacheFormData = {
        ...formData,
        intensity: formData.intensity as 'leve' | 'moderada' | 'fuerte',
        medication_taken: formData.medication_taken,
        location: formData.location || undefined,
        medication: formData.medication_taken ? formData.medication : undefined,
        medication_time: formData.medication_taken ? medicationTime : undefined,
        date: formData.date ? formData.date + ':00' : undefined,
      };

      await onSubmit(submitData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="headache-form">
      {error && (
        <div className="form-error">
          {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="date">Fecha y hora</label>
        <input
          type="datetime-local"
          id="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Intensidad *</label>
        <div className="intensity-pills">
          <button
            type="button"
            className={`intensity-pill leve ${formData.intensity === 'leve' ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, intensity: 'leve' }))}
          >
            <span className="intensity-icon">😊</span>
            <span className="intensity-label">Leve</span>
          </button>
          <button
            type="button"
            className={`intensity-pill moderada ${formData.intensity === 'moderada' ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, intensity: 'moderada' }))}
          >
            <span className="intensity-icon">😐</span>
            <span className="intensity-label">Moderada</span>
          </button>
          <button
            type="button"
            className={`intensity-pill fuerte ${formData.intensity === 'fuerte' ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, intensity: 'fuerte' }))}
          >
            <span className="intensity-icon">😣</span>
            <span className="intensity-label">Fuerte</span>
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="location">Ubicación del dolor</label>
        <div className="location-pills-container">
          {['Frente', 'Sien', 'Parte Superior', 'Nuca', 'Toda la Cabeza'].map(location => {
            // Dividir por coma con o sin espacio para manejar ambos formatos
            const selectedLocations = formData.location ? formData.location.split(/,\s*/).map(loc => loc.trim()) : [];
            const isSelected = selectedLocations.includes(location);
            const isTodaLaCabezaSelected = selectedLocations.includes('Toda la Cabeza');
            const isSpecificLocation = location !== 'Toda la Cabeza';
            const isGrayedOut = isTodaLaCabezaSelected && isSpecificLocation;
            
            return (
              <button
                key={location}
                type="button"
                className={`location-pill ${isSelected ? 'selected' : ''} ${isGrayedOut ? 'grayed-out' : ''}`}
                onClick={() => {
                  const currentLocations = formData.location ? formData.location.split(/,\s*/).map(loc => loc.trim()) : [];
                  let newLocations: string[];
                  
                  if (location === 'Toda la Cabeza') {
                    // Si es "Toda la Cabeza"
                    if (isSelected) {
                      // Si está seleccionado, deseleccionar todo
                      newLocations = [];
                    } else {
                      // Si no está seleccionado, seleccionar todas las ubicaciones
                      newLocations = ['Frente', 'Sien', 'Parte Superior', 'Nuca', 'Toda la Cabeza'];
                    }
                  } else {
                    // Si es una ubicación específica
                    if (isTodaLaCabezaSelected) {
                      // Si "Toda la Cabeza" está seleccionado, no permitir cambios en ubicaciones específicas
                      return;
                    }
                    
                    if (isSelected) {
                      newLocations = currentLocations.filter(loc => loc !== location);
                    } else {
                      newLocations = [...currentLocations, location];
                    }
                  }
                  
                  setFormData(prev => ({
                    ...prev,
                    location: newLocations.join(', ')
                  }));
                }}
              >
                <span className="pill-icon">{getLocationIcon(location)}</span>
                <span className="pill-text">{location}</span>
                {isSelected && (
                  <span className="pill-check">✓</span>
                )}
              </button>
            );
          })}
        </div>
        <small className="form-help-pills">
          💡 Selecciona las zonas donde sientes dolor
        </small>
      </div>

      <div className="form-group">
        <label>¿Tomaste medicación? *</label>
        <div className="medication-toggle">
          <button
            type="button"
            className={`medication-option no ${formData.medication_taken === false ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, medication_taken: false }))}
          >
            <span>❌</span> No
          </button>
          <button
            type="button"
            className={`medication-option yes ${formData.medication_taken === true ? 'selected' : ''}`}
            onClick={() => setFormData(prev => ({ ...prev, medication_taken: true }))}
          >
            <span>✅</span> Sí
          </button>
        </div>
      </div>

      {formData.medication_taken && (
        <>
          <div className="form-group">
            <label htmlFor="medication">¿Qué medicamento tomaste?</label>
            <MedicationAutocomplete
              value={formData.medication || ''}
              onChange={(value) => setFormData(prev => ({ ...prev, medication: value }))}
              placeholder="Ej: Paracetamol, Ibuprofeno 600mg..."
              id="medication"
              name="medication"
            />
          </div>

          <div className="form-group">
            <label htmlFor="medication_time">¿A qué hora lo tomaste?</label>
            <input
              type="time"
              id="medication_time"
              name="medication_time"
              value={formData.medication_time}
              onChange={handleChange}
            />
          </div>
        </>
      )}


      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar Registro'}
        </button>
      </div>
    </form>
  );
};

// Función helper para iconos de ubicación
const getLocationIcon = (location: string) => {
  switch (location) {
    case 'Frente':
      return '👁️';
    case 'Sien':
      return '⚡';
    case 'Parte Superior':
      return '⬆️';
    case 'Nuca':
      return '🔙';
    case 'Toda la Cabeza':
      return '🤕';
    default:
      return '📍';
  }
};

export default HeadacheForm;